import {
  EncryptedPDFError,
  PDFDocument,
  PDFFont,
  StandardFonts,
  degrees,
  type PDFImage,
  type PDFPage,
} from '@cantoo/pdf-lib';
import { formatSignatureTimestamp } from '../../signature/timestamp';
import {
  PdfBox,
  PdfPlacement,
  STAMP_FONT_SIZE,
  STAMP_LINE_HEIGHT,
  getPlacementSize,
  getStampTextLayout,
  getVisualSize,
  normalizeRotation,
  toPdfPoint,
} from './pdf-placement';

export type PdfSignErrorReason = 'password' | 'invalid';

export class PdfSignError extends Error {
  constructor(public readonly reason: PdfSignErrorReason, options?: { cause?: unknown }) {
    super(`Unable to sign PDF: ${reason}`);
    this.name = 'PdfSignError';
    if (options?.cause) this.cause = options.cause;
  }
}

/** Opens the original; owner-password-only files are decrypted (pdf-lib drops `/Encrypt` once decrypted). */
const loadOriginal = async (bytes: Uint8Array) => {
  try {
    return await PDFDocument.load(bytes);
  } catch (error) {
    if (!(error instanceof EncryptedPDFError)) throw new PdfSignError('invalid', { cause: error });
  }

  let pdf: PDFDocument;
  try {
    pdf = await PDFDocument.load(bytes, { password: '' });
  } catch (error) {
    throw new PdfSignError('password', { cause: error });
  }
  // Still encrypted means a user password is required to read the content.
  if (pdf.isEncrypted) throw new PdfSignError('password');
  return pdf;
};

/** The box pdf.js displays: CropBox clipped to MediaBox. */
const getDisplayBox = (page: PDFPage): PdfBox => {
  const media = page.getMediaBox();
  const crop = page.getCropBox();
  const x0 = Math.max(media.x, crop.x);
  const y0 = Math.max(media.y, crop.y);
  const x1 = Math.min(media.x + media.width, crop.x + crop.width);
  const y1 = Math.min(media.y + media.height, crop.y + crop.height);
  if (x1 <= x0 || y1 <= y0) return { x0: media.x, y0: media.y, x1: media.x + media.width, y1: media.y + media.height };
  return { x0, y0, x1, y1 };
};

/** Standard fonts only encode WinAnsi; replace anything else so drawText never throws on a name. */
const createSanitizer = (font: PDFFont) => {
  const supported = new Set(font.getCharacterSet());
  return (text: string) =>
    Array.from(text)
      .map((char) => (supported.has(char.codePointAt(0) ?? -1) ? char : '?'))
      .join('');
};

const sha256Hex = async (bytes: Uint8Array) => {
  if (typeof crypto === 'undefined' || !crypto.subtle) return undefined;
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
};

interface GenerateSignedPdfArgs {
  original: Uint8Array;
  placements: PdfPlacement[];
  signedAt: string;
  labels: { signedBy: (name: string) => string; date: (timestamp: string) => string };
}

/**
 * Flattens each placement onto the original PDF with a "Digitally signed by" / "Date" stamp below it, and records
 * the SHA-256 of the original in the document keywords so the signed copy can be traced back to its source.
 */
export const generateSignedPdf = async ({ original, placements, signedAt, labels }: GenerateSignedPdfArgs) => {
  // Hash before pdf-lib touches the buffer.
  const originalHash = await sha256Hex(original);
  const pdf = await loadOriginal(original);
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const sanitize = createSanitizer(font);
  const images = new Map<string, PDFImage>();
  const pages = pdf.getPages();
  const formattedDate = formatSignatureTimestamp(signedAt);

  for (const placement of placements) {
    const page = pages[placement.pageIndex];
    if (!page) continue;

    let image = images.get(placement.dataUrl);
    if (!image) {
      image = await pdf.embedPng(placement.dataUrl);
      images.set(placement.dataUrl, image);
    }

    const box = getDisplayBox(page);
    const rotation = normalizeRotation(page.getRotation().angle);
    const visual = getVisualSize(box, rotation);
    const rotate = degrees(rotation);
    const { w, h } = getPlacementSize(placement);
    const px = placement.x * visual.width;
    const py = placement.y * visual.height;
    const width = w * visual.width;
    const height = h * visual.height;

    const imageOrigin = toPdfPoint(box, rotation, px, py + height);
    page.drawImage(image, { ...imageOrigin, width, height, rotate });

    const lines = [labels.signedBy(sanitize(placement.signedBy)), labels.date(formattedDate)].map(sanitize);
    const textWidth = Math.max(...lines.map((line) => font.widthOfTextAtSize(line, STAMP_FONT_SIZE)));
    const stamp = getStampTextLayout(px, visual.width, textWidth);
    lines.forEach((line, index) => {
      const baseline = toPdfPoint(box, rotation, stamp.x, py + height + STAMP_LINE_HEIGHT * (index + 1));
      page.drawText(line, { ...baseline, size: stamp.size, font, rotate });
    });
  }

  if (originalHash) {
    const existing = pdf.getKeywords();
    pdf.setKeywords(existing ? [existing, `original-sha256:${originalHash}`] : [`original-sha256:${originalHash}`]);
  }
  pdf.setModificationDate(new Date(signedAt));

  return pdf.save({ useObjectStreams: true });
};
